<template>
  <!-- Kalendarz dni, w których są dokumenty. Klikalne są tylko takie dni - przeglądanie
       pustych dat jeden po drugim w poszukiwaniu dokumentu to strata czasu. -->
  <Teleport to="body">
  <div class="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="subiekt-dni-title">
    <div class="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" @click="$emit('close')"></div>

    <div class="relative flex subiekt-dni-panel w-full max-w-4xl flex-col overflow-hidden rounded-t-xl bg-gray-50 shadow-xl sm:rounded-xl">
      <div class="flex items-start gap-4 border-b border-gray-200 bg-white px-6 py-4">
        <span class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
          <CalendarIcon class="h-5 w-5" aria-hidden="true" />
        </span>
        <div class="min-w-0 flex-1">
          <h3 id="subiekt-dni-title" class="text-base font-semibold text-gray-900">Utworzone dokumenty</h3>
          <p class="mt-0.5 text-sm text-gray-500">Wybierz dzień — aktywne są tylko dni, w których są dokumenty.</p>
        </div>
        <button @click="$emit('close')" type="button"
          class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition duration-200 hover:bg-gray-100 hover:text-gray-700">
          <span class="sr-only">Zamknij</span>
          <XIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div class="flex min-h-0 flex-1 flex-col md:flex-row">
        <!-- KALENDARZ -->
        <div class="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-5 md:w-96 md:border-b-0 md:border-r">
          <div class="flex items-center justify-between">
            <button @click="shiftMonth(-1)" type="button" :disabled="!canGoBack"
              :class="[canGoBack ? 'hover:bg-gray-100 hover:text-gray-700' : 'opacity-30 cursor-not-allowed', 'flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition duration-200']">
              <ChevronLeftIcon class="h-5 w-5" aria-hidden="true" />
            </button>
            <p class="text-sm font-semibold text-gray-900">{{ monthLabel }}</p>
            <button @click="shiftMonth(1)" type="button" :disabled="!canGoForward"
              :class="[canGoForward ? 'hover:bg-gray-100 hover:text-gray-700' : 'opacity-30 cursor-not-allowed', 'flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition duration-200']">
              <ChevronRightIcon class="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div class="mt-4 grid grid-cols-7 text-center text-xs font-semibold uppercase text-gray-400">
            <span v-for="name in weekdays" :key="name">{{ name }}</span>
          </div>
          <div v-if="loading" class="mt-2 grid grid-cols-7 gap-1">
            <div v-for="n in 35" :key="n" class="h-11 animate-pulse rounded-md bg-gray-100"></div>
          </div>
          <div v-else class="mt-2 grid grid-cols-7 gap-1">
            <template v-for="cell in calendarCells" :key="cell.key">
              <span v-if="!cell.day" class="h-11"></span>
              <button v-else-if="cell.info" @click="pick(cell.day)" type="button" v-tooltip="cellTooltip(cell.info)"
                :class="[cell.day === current ? 'bg-indigo-600 text-white' : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-200 hover:ring-indigo-400 hover:bg-indigo-50',
                         'relative flex h-11 flex-col items-center justify-center rounded-md text-sm font-semibold transition duration-150']">
                {{ cell.number }}
                <span class="mt-0.5 flex gap-0.5">
                  <span v-for="dot in dotsOf(cell.info)" :key="dot" :class="[dot, 'h-1.5 w-1.5 rounded-full']"></span>
                </span>
              </button>
              <span v-else :class="[cell.day === today ? 'ring-1 ring-inset ring-gray-300' : '', 'flex h-11 items-center justify-center rounded-md text-sm text-gray-300']">{{ cell.number }}</span>
            </template>
          </div>

          <div class="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
            <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-full bg-indigo-500"></span>do wysłania</span>
            <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-full bg-blue-500"></span>w kolejce</span>
            <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-full bg-green-500"></span>wystawione</span>
            <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-full bg-red-500"></span>błędy</span>
          </div>
        </div>

        <!-- LISTA DNI MIESIĄCA -->
        <div class="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          <p class="text-xs font-semibold uppercase text-gray-500">
            {{ monthDays.length }} {{ monthDays.length === 1 ? 'dzień' : 'dni' }} z dokumentami w tym miesiącu
          </p>
          <p v-if="loadError" class="mt-3 text-sm text-red-700">{{ loadError }}</p>
          <p v-else-if="!loading && monthDays.length === 0" class="mt-3 rounded-lg border-2 border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
            W tym miesiącu nie ma dokumentów.
          </p>
          <ul class="mt-2 space-y-2">
            <li v-for="info in monthDays" :key="info.dzien">
              <button @click="pick(info.dzien)" type="button"
                :class="[info.dzien === current ? 'border-indigo-500 ring-1 ring-indigo-500' : 'hover:border-indigo-300', 'w-full rounded-lg border bg-white px-4 py-3 text-left shadow-sm transition duration-150']">
                <div class="flex items-center justify-between gap-2">
                  <span class="text-sm font-semibold text-gray-900">{{ longDate(info.dzien) }}</span>
                  <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700 doc-tabular">{{ info.razem }} dok.</span>
                </div>
                <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <span v-for="(count, type) in info.typy" :key="type" :class="[typeBadge(type), 'rounded px-1.5 py-0.5 font-mono text-xs font-semibold']">{{ type }} {{ count }}</span>
                  <span v-if="info.wystawione || info.wKolejce || info.doWyslania || info.bledy" class="mx-1 h-3 w-px bg-gray-200"></span>
                  <span v-if="info.wystawione" class="text-xs font-medium text-green-700">{{ info.wystawione }} wystawionych</span>
                  <span v-if="info.wKolejce" class="text-xs font-medium text-blue-700">{{ info.wKolejce }} w kolejce</span>
                  <span v-if="info.doWyslania" class="text-xs font-medium text-indigo-700">{{ info.doWyslania }} do wysłania</span>
                  <span v-if="info.bledy" class="text-xs font-medium text-red-700">{{ info.bledy }} z błędem</span>
                </div>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import moment from 'moment';
import { XIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/solid';
import { subiektPost } from '@/subiektApi';
import { typeBadge } from '@/subiektUi';

export default {
  name: 'SubiektDaysModal',
  components: { XIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon },
  props: {
    // Dzień otwarty w panelu - od niego startuje kalendarz.
    current: { type: String, required: true },
  },
  emits: ['close', 'pick'],
  data() {
    return {
      month: moment(this.current, 'YYYY-MM-DD').startOf('month').format('YYYY-MM'),
      days: [],
      loading: true,
      loadError: '',
      weekdays: ['pn', 'wt', 'śr', 'cz', 'pt', 'so', 'nd'],
    };
  },
  computed: {
    today() {
      return moment().format('YYYY-MM-DD');
    },
    byDay() {
      const map = {};
      for (const info of this.days) map[info.dzien] = info;
      return map;
    },
    monthLabel() {
      const text = moment(this.month, 'YYYY-MM').locale('pl').format('MMMM YYYY');
      return text.charAt(0).toUpperCase() + text.slice(1);
    },
    // Miesiące, w których w ogóle są dokumenty - strzałki nie prowadzą w pustkę.
    months() {
      return [...new Set(this.days.map(info => info.dzien.slice(0, 7)))].sort();
    },
    canGoBack() {
      return this.months.some(m => m < this.month);
    },
    canGoForward() {
      return this.months.some(m => m > this.month);
    },
    monthDays() {
      return this.days.filter(info => info.dzien.startsWith(this.month));
    },
    calendarCells() {
      const start = moment(this.month, 'YYYY-MM').startOf('month');
      const offset = (start.isoWeekday() + 6) % 7;
      const cells = [];
      for (let i = 0; i < offset; i++) cells.push({ key: `pad-${i}` });
      for (let d = 1; d <= start.daysInMonth(); d++) {
        const day = start.clone().date(d).format('YYYY-MM-DD');
        cells.push({ key: day, day, number: d, info: this.byDay[day] || null });
      }
      return cells;
    },
  },
  methods: {
    typeBadge,
    longDate(day) {
      const text = moment(day, 'YYYY-MM-DD').locale('pl').format('dddd, D MMMM');
      return text.charAt(0).toUpperCase() + text.slice(1);
    },
    dotsOf(info) {
      const dots = [];
      if (info.doWyslania) dots.push('bg-indigo-500');
      if (info.wKolejce) dots.push('bg-blue-500');
      if (info.wystawione) dots.push('bg-green-500');
      if (info.bledy) dots.push('bg-red-500');
      return dots.length > 0 ? dots : ['bg-gray-300'];
    },
    cellTooltip(info) {
      return `${info.razem} dok.: ` + Object.entries(info.typy).map(([t, c]) => `${t} ${c}`).join(', ');
    },
    // Przejście do sąsiedniego miesiąca, w którym są dokumenty (pomija puste).
    shiftMonth(direction) {
      const candidates = direction < 0
        ? this.months.filter(m => m < this.month).reverse()
        : this.months.filter(m => m > this.month);
      if (candidates.length > 0) this.month = candidates[0];
    },
    pick(day) {
      this.$emit('pick', day);
    },
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        const res = await subiektPost(this.$store.state.apiLink, '/subiekt/dni', {});
        if (!res.data.success) throw res.data.log || res.data.error;
        this.days = res.data.dni || [];
        // Otwarty miesiąc bez dokumentów - przeskakujemy na najbliższy z dokumentami.
        if (this.monthDays.length === 0 && this.days.length > 0) this.month = this.days[0].dzien.slice(0, 7);
      } catch (error) {
        console.log(error);
        this.loadError = error?.response?.status === 404
          ? 'Serwer nie zna jeszcze tej funkcji — wymaga aktualizacji systemu na serwerze.'
          : (typeof error === 'string' ? error : 'Nie udało się pobrać dni z dokumentami.');
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    moment.locale('pl');
    this.load();
  },
};
</script>

<style scoped>
.subiekt-dni-panel {
  max-height: 88vh;
  min-height: 30rem;
}
.doc-tabular {
  font-variant-numeric: tabular-nums;
}
</style>
